import { formatCaseNumber } from "@/api/cases/caseNumber";
import { shouldReadOfflineCases } from "@/api/cases/offlineCaseMerge";
import { supabase } from "@/api/supabaseClient";
import { isOfflineOnline } from "@/offline/networkFlag";
import { getOfflineStore } from "@/offline/store";
import { mapDateServerMessage } from "@/utils/isoDate";
import { Platform } from "react-native";

function throwRpcError(error: { message?: string }): never {
  if (typeof error.message === "string") {
    const mapped = mapDateServerMessage(error.message);
    if (mapped !== error.message) error.message = mapped;
  }
  throw error;
}

export { formatCaseNumber };

export type CaseStatus =
  | "in_care"
  | "hibernating"
  | "released"
  | "deceased"
  | "voided";
export type AnimalSex = "male" | "female" | "unknown";
export type AgeClass = "baby" | "juvenile" | "adult" | "unclear";

export type AnimalCaseRow = {
  id: string;
  organization_id: string;
  identity_id: string;
  case_year: number;
  case_seq: number;
  case_number: string | null;
  status: CaseStatus;
  name: string | null;
  sex: AnimalSex;
  age_class: AgeClass;
  found_on: string | null;
  found_place_id: string | null;
  found_place_text: string | null;
  intake_reason_note: string | null;
  general_notes: string | null;
  current_care_site_id: string | null;
  current_place_id: string | null;
  organization_species_id: string | null;
  species_free_text: string | null;
  /** Anzeigename aus organization_species, auch wenn die Art deaktiviert ist. */
  species_name?: string | null;
  /** Freitext ohne Katalogzuordnung. */
  species_unassigned?: boolean;
  client_create_id: string | null;
  client_batch_id?: string | null;
  created_at: string;
  updated_at: string;
  /** Nur gesetzt, wenn der Fall lokal in der Warteschlange liegt. */
  pendingSync?: boolean;
};

export type IntakeReason = {
  id: string;
  code: string;
  label: string;
  sort_order: number;
};

export type CreateAnimalCaseInput = {
  organizationId: string;
  clientCreateId: string;
  careSiteId: string;
  placeId?: string | null;
  name?: string | null;
  sex?: AnimalSex;
  ageClass?: AgeClass;
  foundOn?: string | null;
  foundPlaceId?: string | null;
  foundPlaceText?: string | null;
  intakeReasonIds?: string[];
  intakeReasonNote?: string | null;
  generalNotes?: string | null;
  organizationSpeciesId?: string | null;
  speciesFreeText?: string | null;
};

/**
 * RPC-Felder nur mit gesetztem Wert. Leere Keys fehlen, damit create_animal_case
 * vor dem db push die alte Signatur trifft (sonst PGRST202).
 */
export function caseSpeciesRpcFields(input: {
  organizationSpeciesId?: string | null;
  speciesFreeText?: string | null;
}): {
  p_organization_species_id?: string;
  p_species_free_text?: string;
} {
  const free = input.speciesFreeText?.trim() ?? "";
  const speciesId = input.organizationSpeciesId?.trim() ?? "";
  const fields: {
    p_organization_species_id?: string;
    p_species_free_text?: string;
  } = {};
  if (speciesId.length > 0) fields.p_organization_species_id = speciesId;
  if (free.length > 0) fields.p_species_free_text = free;
  return fields;
}

export function batchAnimalSpeciesFields(input: {
  organizationSpeciesId?: string | null;
  speciesFreeText?: string | null;
}): {
  organization_species_id?: string;
  species_free_text?: string;
} {
  const fields = caseSpeciesRpcFields(input);
  const batch: {
    organization_species_id?: string;
    species_free_text?: string;
  } = {};
  if (fields.p_organization_species_id) {
    batch.organization_species_id = fields.p_organization_species_id;
  }
  if (fields.p_species_free_text) batch.species_free_text = fields.p_species_free_text;
  return batch;
}

/** Ein Individuum in der Sammelaufnahme (Abweichungen vom gemeinsamen Vorbelegen). */
export type BatchAnimalInput = {
  clientCreateId: string;
  careSiteId: string;
  placeId?: string | null;
  name?: string | null;
  sex?: AnimalSex;
  ageClass?: AgeClass;
  foundOn?: string | null;
  foundPlaceId?: string | null;
  foundPlaceText?: string | null;
  intakeReasonIds?: string[];
  intakeReasonNote?: string | null;
  generalNotes?: string | null;
  organizationSpeciesId?: string | null;
  speciesFreeText?: string | null;
};

export type CreateAnimalCasesBatchInput = {
  organizationId: string;
  clientBatchId: string;
  animals: BatchAnimalInput[];
  linkAsSiblings?: boolean;
};

export type CaseListItem = AnimalCaseRow & {
  care_site_name?: string | null;
  found_place_name?: string | null;
};

type SpeciesLabelRow = {
  organization_species_id?: string | null;
  species_free_text?: string | null;
  species_name?: string | null;
  species_unassigned?: boolean;
};

/**
 * Vor dem db push kennt PostgREST die Tabelle oder die FK nicht.
 * PGRST205/42P01: Tabelle fehlt. PGRST200: Embed-Relation fehlt.
 * Andere Fehler bleiben Fehler.
 */
export function organizationSpeciesLookupMissing(error: {
  code?: string | null;
  message?: string | null;
} | null): boolean {
  if (!error) return false;
  const code = error.code ?? "";
  if (code === "PGRST200" || code === "PGRST205" || code === "42P01") return true;
  const message = (error.message ?? "").toLowerCase();
  return (
    message.includes("organization_species") &&
    (message.includes("schema cache") ||
      message.includes("does not exist") ||
      message.includes("could not find"))
  );
}

/** Namen nachladen, ohne die Fälle an die FK zu embedden. */
export async function withSpeciesNames<T extends SpeciesLabelRow>(rows: T[]): Promise<T[]> {
  const ids = [
    ...new Set(
      rows
        .map((row) => row.organization_species_id)
        .filter((id): id is string => typeof id === "string" && id.length > 0)
    ),
  ];
  const names = new Map<string, string>();
  if (ids.length > 0) {
    const { data, error } = await supabase
      .from("organization_species")
      .select("id, name")
      .in("id", ids);
    if (error) {
      if (!organizationSpeciesLookupMissing(error)) throw error;
    } else {
      for (const row of (data ?? []) as { id?: string; name?: string | null }[]) {
        if (row.id && row.name) names.set(row.id, row.name);
      }
    }
  }
  return rows.map((row) => ({
    ...row,
    species_name: row.organization_species_id
      ? (names.get(row.organization_species_id) ?? null)
      : null,
    species_unassigned: Boolean(row.species_free_text),
  }));
}

export class CasesApi {
  static async listMyCases(): Promise<CaseListItem[]> {
    let rows: CaseListItem[] = [];
    if (isOfflineOnline()) {
      const { data, error } = await supabase
        .from("animal_cases")
        .select(
          `
        *,
        care_site:care_sites ( name ),
        found_place:geo_places ( name, city, plz )
      `
        )
        .neq("status", "voided")
        .order("created_at", { ascending: false });
      if (error) throw error;

      rows = await withSpeciesNames(
        (data ?? []).map((row: any) => {
          const site = Array.isArray(row.care_site) ? row.care_site[0] : row.care_site;
          const place = Array.isArray(row.found_place)
            ? row.found_place[0]
            : row.found_place;
          const { care_site: _cs, found_place: _fp, ...rest } = row;
          return {
            ...rest,
            care_site_name: site?.name ?? null,
            found_place_name: place
              ? [place.name, place.city, place.plz].filter(Boolean).join(", ")
              : null,
          } as CaseListItem;
        })
      );
    }

    // Web: der SQLite-Worker lehnt nie ab (Antwort text/html). Ohne diese
    // Schranke bleibt useMyCases auf isLoading und die Suche auf „Lade Tiere…“.
    if (shouldReadOfflineCases(Platform.OS)) {
      try {
        const store = await getOfflineStore();
        const local = await store.listLocalCases();
        const seen = new Set(rows.map((r) => r.id));
        for (const item of local) {
          if (seen.has(item.id) || (item.client_create_id && seen.has(item.client_create_id))) {
            continue;
          }
          rows.unshift({
            id: item.id,
            organization_id: item.organization_id,
            identity_id: item.identity_id ?? "",
            case_year: item.case_year ?? new Date().getFullYear(),
            case_seq: item.case_seq ?? 0,
            case_number: item.case_number,
            status: (item.status as CaseStatus) ?? "in_care",
            name: item.name,
            sex: (item.sex as AnimalSex) ?? "unknown",
            age_class: (item.age_class as AgeClass) ?? "unclear",
            found_on: item.found_on ?? null,
            found_place_id: null,
            found_place_text: null,
            intake_reason_note: null,
            general_notes: null,
            current_care_site_id: item.current_care_site_id,
            current_place_id: item.current_place_id,
            organization_species_id: item.organization_species_id ?? null,
            species_free_text: item.species_free_text ?? null,
            species_name: null,
            species_unassigned: Boolean(item.species_free_text),
            client_create_id: item.client_create_id,
            created_at: item.created_at,
            updated_at: item.updated_at ?? item.created_at,
          });
        }
      } catch {
        /* store optional */
      }
    }

    return rows;
  }

  static async getCase(caseId: string): Promise<CaseListItem | null> {
    const { data, error } = await supabase
      .from("animal_cases")
      .select(
        `
        *,
        care_site:care_sites ( name ),
        found_place:geo_places ( name, city, state, county, plz, place_type )
      `
      )
      .eq("id", caseId)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;

    const row: any = data;
    const site = Array.isArray(row.care_site) ? row.care_site[0] : row.care_site;
    const place = Array.isArray(row.found_place)
      ? row.found_place[0]
      : row.found_place;
    const { care_site: _cs, found_place: _fp, ...rest } = row;
    const [labeled] = await withSpeciesNames([
      {
        ...rest,
        care_site_name: site?.name ?? null,
        found_place_name: place
          ? [place.name, place.city, place.county, place.state, place.plz]
              .filter(Boolean)
              .join(", ")
          : null,
      } as CaseListItem,
    ]);
    return labeled;
  }

  static async listIntakeReasons(organizationId: string): Promise<IntakeReason[]> {
    const { data, error } = await supabase
      .from("intake_reason_catalog")
      .select("id, code, label, sort_order")
      .eq("organization_id", organizationId)
      .eq("is_active", true)
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return (data ?? []) as IntakeReason[];
  }

  static async listCaseIntakeReasonIds(caseId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from("case_intake_reasons")
      .select("reason_id")
      .eq("case_id", caseId);
    if (error) throw error;
    return (data ?? []).map((r: { reason_id: string }) => r.reason_id);
  }

  static async createCaseOnline(input: CreateAnimalCaseInput): Promise<AnimalCaseRow> {
    const { data, error } = await supabase.rpc("create_animal_case", {
      p_organization_id: input.organizationId,
      p_client_create_id: input.clientCreateId,
      p_care_site_id: input.careSiteId,
      p_place_id: input.placeId ?? null,
      p_name: input.name ?? null,
      p_sex: input.sex ?? "unknown",
      p_age_class: input.ageClass ?? "unclear",
      p_found_on: input.foundOn ?? null,
      p_found_place_id: input.foundPlaceId ?? null,
      p_found_place_text: input.foundPlaceText ?? null,
      p_intake_reason_ids: input.intakeReasonIds?.length
        ? input.intakeReasonIds
        : null,
      p_intake_reason_note: input.intakeReasonNote ?? null,
      p_general_notes: input.generalNotes ?? null,
      ...caseSpeciesRpcFields(input),
    });
    if (error) throwRpcError(error);
    return data as AnimalCaseRow;
  }

  static async createCase(input: CreateAnimalCaseInput): Promise<AnimalCaseRow> {
    const { createCaseViaGateway } = await import("@/offline/sync/gateway");
    return createCaseViaGateway(input);
  }

  static async createCasesBatchOnline(
    input: CreateAnimalCasesBatchInput
  ): Promise<AnimalCaseRow[]> {
    const animals = input.animals.map((a) => ({
      client_create_id: a.clientCreateId,
      care_site_id: a.careSiteId,
      place_id: a.placeId ?? null,
      name: a.name ?? null,
      sex: a.sex ?? "unknown",
      age_class: a.ageClass ?? "unclear",
      found_on: a.foundOn ?? null,
      found_place_id: a.foundPlaceId ?? null,
      found_place_text: a.foundPlaceText ?? null,
      intake_reason_ids: a.intakeReasonIds?.length ? a.intakeReasonIds : [],
      intake_reason_note: a.intakeReasonNote ?? null,
      general_notes: a.generalNotes ?? null,
      ...batchAnimalSpeciesFields(a),
    }));

    const { data, error } = await supabase.rpc("create_animal_cases_batch", {
      p_organization_id: input.organizationId,
      p_client_batch_id: input.clientBatchId,
      p_animals: animals,
      p_link_as_siblings: input.linkAsSiblings ?? false,
    });
    if (error) throwRpcError(error);
    return (data ?? []) as AnimalCaseRow[];
  }

  static async createCasesBatch(
    input: CreateAnimalCasesBatchInput
  ): Promise<AnimalCaseRow[]> {
    const { createCasesBatchViaGateway } = await import(
      "@/offline/sync/gateway"
    );
    return createCasesBatchViaGateway(input);
  }

  static async updateCaseMasterRpc(input: {
    caseId: string;
    name?: string | null;
    sex?: AnimalSex;
    ageClass?: AgeClass;
    foundOn?: string | null;
    clearFoundOn?: boolean;
    foundPlaceId?: string | null;
    clearFoundPlace?: boolean;
    foundPlaceText?: string | null;
    intakeReasonIds?: string[];
    replaceIntakeReasons?: boolean;
    intakeReasonNote?: string | null;
    generalNotes?: string | null;
    careSiteId?: string | null;
    placeId?: string | null;
    updateHousing?: boolean;
  }): Promise<AnimalCaseRow> {
    const { data, error } = await supabase.rpc("update_animal_case_master", {
      p_case_id: input.caseId,
      p_name: input.name !== undefined ? input.name : null,
      p_sex: input.sex ?? null,
      p_age_class: input.ageClass ?? null,
      p_found_on: input.foundOn ?? null,
      p_clear_found_on: input.clearFoundOn ?? false,
      p_found_place_id: input.foundPlaceId ?? null,
      p_clear_found_place: input.clearFoundPlace ?? false,
      p_found_place_text:
        input.foundPlaceText !== undefined ? input.foundPlaceText : null,
      p_intake_reason_ids: input.replaceIntakeReasons
        ? input.intakeReasonIds ?? []
        : null,
      p_replace_intake_reasons: input.replaceIntakeReasons ?? false,
      p_intake_reason_note:
        input.intakeReasonNote !== undefined ? input.intakeReasonNote : null,
      p_general_notes:
        input.generalNotes !== undefined ? input.generalNotes : null,
      p_care_site_id: input.careSiteId ?? null,
      p_place_id: input.placeId ?? null,
      p_update_housing: input.updateHousing ?? false,
    });
    if (error) throwRpcError(error);
    return data as AnimalCaseRow;
  }
}
